<template>
  <GenericTaskCreationStep
    class="upload-task-file-dialog"
    v-on="$listeners"
    :disableNextButton="
      !useSessionInventoryFile &&
      !(selectedInputFile && selectedInputFileFormat)
    "
  >
    <template v-slot:title>Select matcher files</template>
    <template>
      <v-row class="align-end">
        <v-col cols="6">
          <UploadsSelectV2
            label="Add or select an input file"
            :uploadType="FileUploadType.UPLOADS"
            :disabled="useSessionInventoryFile || isInitializing"
            :selected="selectedInputFile"
            v-on:uploadSelect="
              (selectedFile) =>
                handleUploadChange(FileUploadType.UPLOADS, selectedFile)
            "
          />
        </v-col>
        <v-col cols="6">
          <v-combobox
            label="Input File Type"
            :disabled="useSessionInventoryFile || !selectedInputFile"
            :value="selectedInputFileFormat"
            v-on:change="
              (value) => $emit('selectedInputFileFormatChange', value)
            "
            :items="inputFileFormatOptions"
            :return-object="false"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-switch
          class="use-business-inventory-file-switch mt-0 mb-4"
          hint="Business inventory file is only available if an import request is selected"
          :persistent-hint="!importRequestId"
          :input-value="useSessionInventoryFile"
          :label="
            useSessionInventoryFile
              ? sessionInventoryFilePath + 'inventory_items.csv'
              : 'Use business inventory_items.csv'
          "
          :disabled="!importRequestId || !sessionInventoryFilePath"
          :loading="isInitializing"
          v-on:change="
            (isToggled) =>
              handleSessionInventoryFileChange(
                this.sessionInventoryFilePath,
                isToggled
              )
          "
        />
      </v-row>
      <h3>Sales history</h3>
      <v-divider horizontal></v-divider>
      <v-row>
        <v-col cols="5.5">
          <UploadsSelectV2
            label="Add or select Sales History"
            :uploadType="FileUploadType.SALES_HISTORIES"
            :selected="selectedHistoryFile"
            v-on:uploadSelect="
              (selectedFile) =>
                handleUploadChange(FileUploadType.SALES_HISTORIES, selectedFile)
            "
          />
        </v-col>
      </v-row>
      <h3>Previous run</h3>
      <v-divider horizontal></v-divider>
      <v-row>
        <v-col cols="5.5">
          <UploadsSelectV2
            label="Add or select a Previous Run file"
            :uploadType="FileUploadType.PREVIOUS_RUNS"
            :selected="selectedPreviousRunFile"
            :disabled="useRecentResult"
            v-on:uploadSelect="
              (selectedFile) =>
                handleUploadChange(FileUploadType.PREVIOUS_RUNS, selectedFile)
            "
          />
          <template v-if="recentResultId">
            <v-switch
              :input-value="useRecentResult"
              label="Use recent previousRun from the copied task"
              v-on:change="(isToggled) => handleRecentResultChange(isToggled)"
            />
          </template>
        </v-col>
      </v-row>
      <v-row dense v-if="recentResultId">
        <v-col cols="6" sm="6">
          <v-switch
            class="mt-0"
            v-model="addRecentResultId"
            label="Use recent previousRun from the copied task"
          />
        </v-col>
      </v-row>
      <h3>Inventory file</h3>
      <v-divider horizontal />
      <v-row>
        <v-col cols="5.5">
          <UploadsSelectV2
            label="Add or select an Inventory File"
            :uploadType="FileUploadType.INVENTORY_FILES"
            :selected="selectedInventoryFile"
            v-on:uploadSelect="
              (selectedFile) =>
                handleUploadChange(FileUploadType.INVENTORY_FILES, selectedFile)
            "
          />
        </v-col>
      </v-row>
    </template>
  </GenericTaskCreationStep>
</template>

<style>
.upload-task-file-dialog {
  width: 600px;
}

.use-business-inventory-file-switch {
  line-break: anywhere;
}
</style>

<script>
import GenericTaskCreationStep from './GenericTaskCreationStep.vue';
import UploadsSelectV2 from '@/components/selects/UploadsSelectV2.vue';

import { businessApiV2 } from '@/api/v2';
import CONFIG from '@/config';
import { FileUploadType } from '@/constants';
import { getFilesTypesByExtension } from '@/helpers';

const PREPROCESSOR_GCS_OUTPUT_DIRECTORY = 'universal';

export default {
  props: {
    importRequestId: String,
    recentResultId: String,
    useRecentResult: Boolean,
    sessionInventoryFilePath: String,
    useSessionInventoryFile: Boolean,
    // Currently selected files from the existing
    selectedInputFileFormat: String,
    selectedInputFile: [Object, File],
    selectedHistoryFile: [Object, File],
    selectedPreviousRunFile: [Object, File],
    selectedInventoryFile: [Object, File]
  },
  components: { GenericTaskCreationStep, UploadsSelectV2 },
  data() {
    return {
      FileUploadType,
      isInitializing: false
    };
  },
  async created() {
    if (this.importRequestId) {
      this.isInitializing = true;
      await this.fetchImportRequestSourceDataLocation(this.importRequestId);
      this.isInitializing = false;
    }
  },
  computed: {
    inputFileFormatOptions() {
      return this.selectedInputFile
        ? getFilesTypesByExtension(this.selectedInputFile.name)
        : [];
    }
  },
  methods: {
    async fetchImportRequestSourceDataLocation(id) {
      const { sourceDataLocation, destinationDataLocation } =
        await businessApiV2.fetchBusinessImportRequestSourceDataLocation(
          id,
          CONFIG.ENV
        );

      if (destinationDataLocation || sourceDataLocation) {
        this.handleSessionInventoryFileChange(
          destinationDataLocation ||
            `${sourceDataLocation}/${PREPROCESSOR_GCS_OUTPUT_DIRECTORY}/`,
          this.useSessionInventoryFile
        );
      }
    },
    handleUploadChange(type, selectedFile) {
      this.$emit('uploadChange', { type, upload: selectedFile });
    },
    handleSessionInventoryFileChange(path, isToggled) {
      this.$emit('useSessionInventoryFileChange', path, isToggled);
    },
    handleRecentResultChange(isToggled) {
      this.$emit('useRecentResultChange', isToggled);
    }
  }
};
</script>
